import { Dispatch, SetStateAction, useEffect } from "react";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import DashboardStyle from "./DashboardStyle";
import WelcomeCard from "./WelcomeCard/WelcomeCard";
import Policies from "./Policies/Policies";
import Claims from "./Claims/Claims";
import { RootState } from "../../store/store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getClaimsData, GetPoliciesData, GetInitBasePrompt } from "./DashboardAction";
import Alerts from "./Alerts/Alerts";
import QuickReferences from "./QuickReferences/QuickReferences";
import {
  getBufferData,
  getCookie,
  isFeatureAvailable,
} from "../../utils/Utility";
import { setDownloadIdCardContent } from "./DashboardSlice";
import { IClaim } from "../../models/user/dashboard/Dashboard";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import { Typed } from "react-typed";
import { IGetInitBasePromptRequestParams } from "../../models/user/InsureAssist/InsureAssist";
import i18n from "../../translator/i18next";
import CMSContent from "./QuickReferences/CMSContent/CMSContent";
import CMSAlertBanner from "./CMSAlertBanner/CMSAlertBanner";
import AgentDetails from "./Agents/AgentDetails";
import { getContent } from "../../admin/CMS/CMSActions";
import { useLocation } from "react-router-dom";
import CustomSkeleton from "../../common/CustomSkeleton/CustomSkeleton";
import { clearClaimsData, resetDashboardState } from "./DashboardSlice";

interface IProps {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isDisabled: boolean;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  typed: Typed | undefined;
  setTyped: Dispatch<SetStateAction<Typed | undefined>>;
}

const Dashboard: React.FC<IProps> = ({
  setIsOpen,
  isDisabled,
  setIsDisabled,
  typed,
  setTyped,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { isError, isSuccess, isLoading, calledReducerType } = useAppSelector(
    (store: RootState) => store.common
  );
  const { downloadIdCardContent, claimsData } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { features } = useAppSelector((state: RootState) => state.features);

  const { content } = useAppSelector(
    (state: RootState) => state.contentManagement
  );

  const location = useLocation();

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  useEffect(() => {
    // Skip legacy dashboard data fetching for demo/test customer 0987
    if (userDetails?.customerId === "0987") {
      return;
    }

    if (
      userDetails &&
      userDetails.customerId &&
      carrier &&
      system
    ) {
      // Clear claims data before fetching new data
      dispatch(resetDashboardState());
      
      dispatch(
        GetPoliciesData({
          customerId: userDetails?.customerId,
          carrier,
          system,
        })
      )
        .then(() =>
          dispatch(
            getClaimsData({
              customerId: userDetails.customerId,
              carrier,
              system,
            })
          )
        )
        .then(() => {
          sessionStorage.removeItem('insureAssistInitResponse');
          const requestParams: IGetInitBasePromptRequestParams = {
            customerId: userDetails.customerId,
            language: i18n.language,
          };
          dispatch(GetInitBasePrompt(requestParams));
        })
        .catch((error) => {
          sessionStorage.removeItem('insureAssistInitResponse');
          const requestParams: IGetInitBasePromptRequestParams = {
            customerId: userDetails.customerId,
            language: i18n.language,
          };
          dispatch(GetInitBasePrompt(requestParams));
        });
    }
  }, [
    userDetails,
    dispatch,
    GetInitBasePrompt,
    carrier,
    system,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetDownloadIdCard" &&
      downloadIdCardContent
    ) {
      const bufferArray = getBufferData(downloadIdCardContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );
      const downloadLink = document.createElement("a");
      downloadLink.download = "Insured ID card.pdf";
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      dispatch(setDownloadIdCardContent(null));
    }
  }, [calledReducerType, isSuccess, downloadIdCardContent, dispatch]);

  useEffect(() => {
    if (isError && calledReducerType === "GetDownloadIdCard") {
      alert(`Error in downloading document`);
      dispatch(resetCommonSlice());
    }
  }, [calledReducerType, isError, dispatch]);

  useEffect(() => {
    dispatch(getContent());
  }, [dispatch]);

  return (
    <Box sx={DashboardStyle.dashboard}>
      <WelcomeCard
        setIsOpen={setIsOpen}
        isDisabled={isDisabled}
        setIsDisabled={setIsDisabled}
        typed={typed}
        setTyped={setTyped}
      />
      {isFeatureAvailable({
        features,
        featureCodes: ["quick_references"],
      }) && (
        <>
          <CMSAlertBanner cmsAlerts={content} />
        </>
      )}
      <Alerts />
      {isLoading && calledReducerType === "dashboard/getClaimsData" ? (
        <CustomSkeleton count={2} direction="column"/>
      ) : isFeatureAvailable({
          featureCodes: ["my_claims"],
          features,
        }) &&
        claimsData &&
        claimsData.claimList &&
        claimsData.claimList.filter(
          (claim: IClaim) => claim.carrierClaimStatus !== "Closed"
        ).length > 0 ? (
        <Claims claimsData={claimsData} />
      ) : (
        <></>
      )}

      {isLoading && calledReducerType === "dashboard/GetPoliciesData" ? (
        <CustomSkeleton count={4} direction={isMobile ? "column" : "row"} height="250px" />
      ) : (
        <Policies />
      )}

      {isLoading && calledReducerType === "dashboard/customer/agents" ? (
        <CustomSkeleton count={4} direction={isMobile ? "column" : "row"} height="250px"/>
      ) : (
        <AgentDetails />
      )}

      {isLoading && calledReducerType === "cms/getContent" ? (
        <CustomSkeleton count={4} direction={isMobile ? "column" : "row"} height="250px"/>
      ) : isFeatureAvailable({
          features,
          featureCodes: ["quick_references"],
        }) &&
        content &&
        content?.length ? (
        <>
          {/* <QuickReferences /> */}
          <CMSContent cmsContent={content} />
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default Dashboard;
